// Work order "shop copy": a printout for the mechanic — what to do, hours, parts with qty and bin. Never a price.
import { useApi, navigate, fmtDate, type Row } from '../lib';
import { Button, Loading, ErrorBox, Icon } from '../ui';

const hrs = (l: Row) => (l.flat ? 'flat' : `${(Number(l.hours) / 100).toFixed(2).replace(/\.?0+$/, '') || '0'} h`);

export function ShopCopy({ id }: { id: string }) {
  const { data, error, reload } = useApi<Row>(`/api/work-orders/${id}`);
  const shop = useApi<Row>('/api/settings');
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const w = data.workOrder as Row, s = (shop.data || {}) as Row;
  const jobs = data.jobs as Row[];
  const labor = (data.labor as Row[]).filter(l => !l.misc && Number(l.rate) >= 0);
  const parts = data.parts as Row[];
  const inJob = (x: Row) => jobs.some(j => j.id === x.job_id);
  const loose = { labor: labor.filter(l => !inJob(l)), parts: parts.filter(p => !inJob(p)) };
  const block = (key: string, title: string, who: string, ls: Row[], ps: Row[], j?: Row) => (
    <section key={key} className="sc-job">
      <div className="sc-job-head"><span className="sc-box" /><b>{title}</b>{who && <span className="sc-who">Mechanic: <b>{who}</b></span>}</div>
      {ls.length > 0 && <table className="sc-tbl"><thead><tr><th>Labor</th><th className="n">Hours</th></tr></thead>
        <tbody>{ls.map(l => <tr key={l.id}><td>{l.description}</td><td className="n">{hrs(l)}</td></tr>)}</tbody></table>}
      {ps.length > 0 && <table className="sc-tbl"><thead><tr><th>Part #</th><th>Part</th><th>Bin</th><th className="n">Qty</th><th className="n">✓</th></tr></thead>
        <tbody>{ps.map(p => <tr key={p.id}><td className="sc-sku">{p.sku || '—'}</td><td>{p.description}</td><td>{p.bin || ''}</td><td className="n"><b>{p.qty}</b>{p.uom && !['ea', 'each'].includes(p.uom) ? ` ${p.uom}` : ''}</td><td className="n"><span className="sc-box sm" /></td></tr>)}</tbody></table>}
      {j?.note && <p className="sc-note"><b>{j.note_public === 0 ? 'Office note' : 'Note'}:</b> {j.note}</p>}
      {j?.mech_note && <p className="sc-note"><b>Mechanic note:</b> {j.mech_note}</p>}
      {j?.waiting_note && j.status === 'waiting' && <p className="sc-note"><b>Waiting for parts:</b> {j.waiting_note}</p>}
      <div className="sc-write"><span>Cause / what you found:</span><i /><i /></div>
      <div className="sc-write"><span>Correction / what you did:</span><i /><i /></div>
    </section>
  );
  const unit = w.unit || w.unit_text;
  return (
    <div className="shop-copy-page">
      <div className="page-head no-print">
        <div><button className="back linkish" onClick={() => navigate(`/work-orders/${id}`)}><Icon name="back" size={16} />{w.number}</button>
          <h1>Shop copy — no prices</h1><p>For the mechanic: the work, hours, parts with quantities and bins. No prices or totals anywhere.</p></div>
        <div className="actions"><Button kind="primary" icon="printer" onClick={() => window.print()}>Print</Button></div>
      </div>
      <div className="shop-copy">
        <header className="sc-head">
          <div><b className="sc-shop">{s.shop_name || ''}</b><small>WORK ORDER — SHOP COPY</small></div>
          <div className="sc-no"><b>{w.number}</b><small>Opened {fmtDate(String(w.created_at).slice(0, 10))}</small>{w.priority === 'Urgent' && <span className="sc-urgent">URGENT</span>}</div>
        </header>
        <div className="sc-info">
          <div><small>Company</small><b>{w.customer_name || '—'}</b></div>
          <div><small>Unit #</small><b>{unit ? '#' + unit : '—'}</b></div>
          <div><small>Truck</small><b>{[w.year, w.make, w.model].filter(Boolean).join(' ') || w.unit_type || '—'}</b></div>
          <div><small>VIN</small><b>{w.vin || '—'}</b></div>
          <div><small>Mileage</small><b>{w.mileage ? Number(w.mileage).toLocaleString() : '—'}</b></div>
          <div><small>Lead mechanic</small><b>{w.assigned_name || '—'}</b></div>
          {w.trailer_id && <div><small>Trailer</small><b>#{w.trailer_unit}{w.trailer_vin ? ` · VIN ${w.trailer_vin}` : ''}</b></div>}
        </div>
        {w.complaint && <div className="sc-complaint"><small>Complaint / requested work</small>{w.complaint}</div>}
        {w.notes && <div className="sc-complaint"><small>Office notes</small>{w.notes}</div>}
        {jobs.map(j => block(j.id, j.title, j.assigned_name || '', labor.filter(l => l.job_id === j.id), parts.filter(p => p.job_id === j.id), j))}
        {(loose.labor.length > 0 || loose.parts.length > 0) && block('loose', 'Other work on this truck', '', loose.labor, loose.parts)}
        {!jobs.length && !labor.length && !parts.length && <p className="muted">No jobs yet.</p>}
        <div className="sc-sign">
          <div><i />Mechanic signature</div><div><i />Time in</div><div><i />Time out</div><div><i />Date</div>
        </div>
      </div>
    </div>
  );
}
